import { Button, Flex, Modal, TextInput } from "@mantine/core";
import { useState } from "react";
import CustomSelect from "../common/Inputs/CustomSelect";
import {
  ShiftTeam,
  createShiftTeam,
  updateShiftTeam,
} from "../../api/shiftRotationApi";
import { createNotification } from "../../utils/useNotification";

export type ShiftTeamFormState = {
  _id?: string;
  name: string;
  code: string;
  departmentId: string | null;
};

export const EMPTY_TEAM_FORM: ShiftTeamFormState = {
  name: "",
  code: "",
  departmentId: null,
};

export const teamFormOf = (team: ShiftTeam): ShiftTeamFormState => ({
  _id: team._id,
  name: team.name,
  code: team.code,
  departmentId: team.departmentId,
});

interface Props {
  opened: boolean;
  onClose: () => void;
  form: ShiftTeamFormState;
  setForm: (form: ShiftTeamFormState) => void;
  departmentOptions: any[];
  /** Departman kilitliyse (departman sayfasından açıldıysa) seçim gösterilmez. */
  lockDepartment?: boolean;
  onSaved: () => void | Promise<void>;
}

/**
 * EKİP FORMU — Ekipler sayfası ile Departman → Ekipler sekmesinin ORTAK modal'ı.
 *
 * İki yerden açılıyor ve ikisinde de aynı üç alan (ad, kod, departman) var;
 * ayrı ayrı yazılsaydı doğrulama, bildirim ve "kod departman içinde benzersiz"
 * açıklaması iki kopya hâlinde sürüklenirdi. Departman sayfasında departman
 * zaten belli olduğu için `lockDepartment` ile alan hiç gösterilmez.
 */
const ShiftTeamFormModal = ({
  opened,
  onClose,
  form,
  setForm,
  departmentOptions,
  lockDepartment = false,
  onSaved,
}: Props) => {
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      createNotification({ success: false, message: "Ekip adı ve kodu gerekli." });
      return;
    }
    if (!form.departmentId) {
      createNotification({ success: false, message: "Bir departman seçin." });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        departmentId: form.departmentId,
      };
      const response: any = form._id
        ? await updateShiftTeam(form._id, payload)
        : await createShiftTeam(payload);

      createNotification({
        success: response?.success,
        message: response?.success
          ? form._id
            ? "Ekip güncellendi."
            : "Ekip oluşturuldu."
          : response?.message ?? "Ekip kaydedilemedi.",
      });

      if (response?.success) {
        onClose();
        await onSaved();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="16px"
      title={form._id ? "Ekibi Düzenle" : "Yeni Ekip"}
    >
      <Flex direction="column" gap={16}>
        <TextInput
          label="Ekip Adı"
          placeholder="A Ekibi"
          withAsterisk
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
        />
        <TextInput
          label="Kod"
          placeholder="A"
          withAsterisk
          description="Departman içinde benzersiz olmalı. Aynı kod başka departmanda kullanılabilir."
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.currentTarget.value })}
        />
        {!lockDepartment && (
          <CustomSelect
            label="Departman"
            placeholder="Departman Seçiniz"
            withAsterisk
            w="100%"
            data={departmentOptions}
            value={form.departmentId}
            onChange={(value: string | null) =>
              setForm({ ...form, departmentId: value })
            }
            searchable
          />
        )}
        <Flex justify="flex-end" gap={10} mt={8}>
          <Button variant="default" onClick={onClose}>
            Vazgeç
          </Button>
          <Button color="#5C26FF" loading={saving} onClick={submit}>
            Kaydet
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ShiftTeamFormModal;
